// @flow
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Debug from 'debug';
import superagent from 'superagent';
import { FormattedMessage } from 'react-intl';
import makeStyles from '@material-ui/core/styles/makeStyles';
import type { TISMessage } from '../../types';
import Config from '../../models/Config';
import ConfigItem from '../Options/Config/ConfigItem';
import Duration from '../ui/Duration';
import Player from './Player';

const debug = Debug('amms:modules:tisPlayer:filePlayer');

const getTrackBoundaries = (track) => {
    let minTime;
    let maxTime;
    for(let i = 0, length = track.AsterixTracks.length; i < length; i += 1) {
        const item = track.AsterixTracks[i];
        const trackTime = new Date(item.TimeOfTrackInformation);

        if(!minTime || trackTime < minTime) {
            minTime = trackTime;
        }
        if(!maxTime || trackTime > maxTime) {
            maxTime = trackTime;
        }
    }

    debug('getTrackDuration minTime', minTime);
    debug('getTrackDuration maxTime', maxTime);

    return {
        minTime,
        maxTime
    };
};

const createPlaylist = (name, track) => {
    const { minTime, maxTime } = getTrackBoundaries(track);

    const messages = [];
    for(let i = 0, length = track.AsterixTracks.length; i < length; i += 1) {
        const item = track.AsterixTracks[i];
        const trackTime = new Date(item.TimeOfTrackInformation);

        messages.push({
            time: trackTime - minTime,
            data: item
        });
    }

    return {
        name: name,
        duration: maxTime - minTime,
        messages
    };
};

const sortMessages = (track) => {
    track.AsterixTracks.sort((a, b) => {
        if(a.TimeOfTrackInformation < b.TimeOfTrackInformation) {
            return -1;
        }
        else if(a.TimeOfTrackInformation > b.TimeOfTrackInformation) {
            return 1;
        }
        else {
            return 0;
        }
    });
    return track;
};

const playMessages = (messages, currentTime, interval, callback) => {
    for(let i = 0, length = messages.length; i < length; i += 1) {
        const message = messages[i];

        if(message.time < currentTime - interval) {
            continue;
        }
        if(message.time > currentTime) {
            break;
        }

        callback(message.data);
    }
};

const useStyles = makeStyles(theme => ({
    player: {
        position: 'absolute',
        top: -19,
        width: '100%'
    },
    content: {
        paddingTop: theme.spacing(2)
    }
}));

type TisPlayerConfig = $PropertyType<ConfigType, 'tisPlayer'>;

type Props = $ReadOnly<{
    playerRef: {},
    connected: boolean,
    config: TisPlayerConfig,
    loopEnabled: boolean,

    playMessage: (message: TISMessage) => void,
    onPlay: () => void,
    onStop: () => void,
}>;
function FilePlayer({ playerRef, connected, config, loopEnabled, playMessage, onPlay, onStop }: Props) {
    const classes = useStyles();
    const [tracks, setTracks] = useState();
    const [currentTrackName, setCurrentTrackName] = useState(config.trackFile);
    const [currentTrack, setCurrentTrack] = useState();
    const [currentTime, setCurrentTime] = useState();
    const innerPlayerRef = useRef();
    const currentTimeRef = useRef(0);

    useEffect(() => {
        superagent
            .get('/api/tisPlayer/tracks')
            .catch(console.error)
            .then(response => {
                setTracks(response.body);
            });
    }, []);

    useEffect(() => setCurrentTrackName(config.trackFile), [config.trackFile]);

    useEffect(() => {
        if(currentTrackName) {
            onStop();

            if(tracks) {
                if(tracks.indexOf(currentTrackName) !== -1) {
                    superagent
                        .get(`/tracks/${currentTrackName}`)
                        .catch(console.error)
                        .then(response => {
                            if(response.body) {
                                currentTimeRef.current = 0;
                                const track = createPlaylist(currentTrackName, sortMessages(response.body));
                                setCurrentTrack(track);

                                debug('Loaded track', track);
                            }
                        });
                }
                else {
                    const newConfig = {
                        ...config,
                        trackFile: null
                    };
                    Config.findByIdAndUpdate('tisPlayer', newConfig, { upsert: true });
                }
            }
        }
        else {
            setCurrentTrack(null);
        }
    }, [config, tracks, onStop, currentTrackName]);

    const handleProgress = useCallback((e) => {
        playMessages(currentTrack.messages, e.currentTime, e.interval, playMessage);
        setCurrentTime(e.currentTime);
    }, [currentTrack, playMessage]);
    const handleTracking = useCallback((e) => {
        playMessages(currentTrack.messages, e.currentTime, 1000, playMessage);
        setCurrentTime(e.currentTime);
    }, [currentTrack, playMessage]);
    const handleEnd = () => {
        if(loopEnabled && innerPlayerRef.current) {
            innerPlayerRef.current.play();
        }
    };

    const getPlayerRef = (ref) => {
        innerPlayerRef.current = ref;
        playerRef(ref);
    };

    return (
        <div className={classes.content}>
            {connected && currentTrack &&
                <Player
                    className={classes.player}
                    playerRef={getPlayerRef}
                    duration={currentTrack.duration}
                    onStart={onPlay}
                    onStop={onStop}
                    onProgress={handleProgress}
                    onTrack={handleTracking}
                    onEnd={handleEnd}
                />
            }

            {tracks &&
                <ConfigItem
                    type="select"
                    label={(
                        <FormattedMessage
                            id="tisPlayer.filePlayer.trackFile"
                            defaultMessage="Track file"
                        />
                    )}
                    path={['tisPlayer', 'trackFile']}
                    items={tracks && tracks.map(trackName => ({
                        value: trackName,
                        label: trackName
                    }))}
                />
            }

            <ConfigItem
                type="boolean"
                label={(
                    <FormattedMessage
                        id="tisPlayer.autoplay"
                        defaultMessage="Autoplay"
                    />
                )}
                path={['tisPlayer', 'autoplay']}
            />

            {currentTrack &&
                <div>
                    Current track: {currentTrack.name} <Duration duration={currentTime}/> / <Duration duration={currentTrack.duration} />
                </div>
            }
        </div>
    );
}

export default FilePlayer;